import React, { useState, useEffect } from "react";
import {
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import ActionButton from "../../components/ActionButton";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import PropertyNames from "../../PropertyNames";
import { useSelector, useDispatch } from "react-redux";
import { setApplicationProperties } from "../../redux/actions/universalActions.js";
import "filepond/dist/filepond.min.css";
import axios from "../../AxiosClient";
import SearchSecondaryInfo from "../../components/search/SearchSecondaryInfo";
import SearchLanguages from "../../components/search/SearchLanguages";
import FormHelperText from "@material-ui/core/FormHelperText";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 15,
    display: "flex",
  },
  container: {
    height: "100%",
    width: "100%",
  },
  flexContainer: {
    display: "flex",
  },
}));

function GeneralSettings() {
  const [saving, setSaving] = useState(false);
  const properties = useSelector((state) => {
    return { ...state.setting.applicationProperties };
  });
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const handleCheckboxChange = (propertyKey) => (e) => {
    dispatch(
      setApplicationProperties({
        ...properties,
        [propertyKey]: e.target.checked ? "true" : "false",
      })
    );
  };

  const updateSearchSecondaryInfo = (secondaryInfo) => {
    dispatch(
      setApplicationProperties({
        ...properties,
        [PropertyNames.SEARCH_SECONDARY_INFO]: JSON.stringify(secondaryInfo),
      })
    );
  };

  const updateLanguages = (languages) => {
    dispatch(
      setApplicationProperties({
        ...properties,
        [PropertyNames.SEARCH_LANGUAGES]: JSON.stringify(languages),
      })
    );
  };

  const save = () => {
    setSaving(true);
    axios
      .patch("/application-property/", {
        [PropertyNames.SEARCH_SECONDARY_INFO]:
          properties[PropertyNames.SEARCH_SECONDARY_INFO],
        [PropertyNames.SEARCH_LANGUAGES]:
          properties[PropertyNames.SEARCH_LANGUAGES],
        [PropertyNames.DISPLAY_NEW_TAB_ICON]:
          properties[PropertyNames.DISPLAY_NEW_TAB_ICON],
        [PropertyNames.DISPLAY_NEW_DOCUMENT_PORTLET_ICON]:
          properties[PropertyNames.DISPLAY_NEW_DOCUMENT_PORTLET_ICON],
      })
      .then(() => {
        enqueueSnackbar(t("properties_updated_snackbar"), {
          variant: "success",
        });
        setSaving(false);
      })
      .catch((err) => {
        enqueueSnackbar(t("properties_updated_error_snackbar"), {
          variant: "error",
        });
        setSaving(false);
      });
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ marginRight: 10 }}>
          {t("general_settings_header")}
        </Typography>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={save}
          loading={saving}
        >
          {t("global_save")}
        </ActionButton>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" gutterBottom>
          {t("search_settings_header")}
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 15 }}>
          {t("search_settings_subheader")}
        </Typography>
        <div style={{ marginBottom: 10 }}>
          <SearchSecondaryInfo
            secondaryInfo={
              properties[PropertyNames.SEARCH_SECONDARY_INFO]
                ? JSON.parse(properties[PropertyNames.SEARCH_SECONDARY_INFO])
                : ["global_topic"]
            }
            onChange={updateSearchSecondaryInfo}
          />
        </div>
        <div>
          <SearchLanguages
            value={
              properties[PropertyNames.SEARCH_LANGUAGES]
                ? JSON.parse(properties[PropertyNames.SEARCH_LANGUAGES])
                : ["en"]
            }
            onChange={updateLanguages}
          />
          <FormHelperText>
            {t("default_search_languages_helper_text_system_config")}
          </FormHelperText>
        </div>
        <Typography variant="h6" gutterBottom>
          {t("portlet_settings_header")}
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 15 }}>
          {t("portlet_settings_subheader")}
        </Typography>
        <FormControl>
          <FormControlLabel
            label={t("display_new_tab_icon")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.DISPLAY_NEW_TAB_ICON] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.DISPLAY_NEW_TAB_ICON
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("display_new_tab_icon_helper_text")}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("display_new_document_portlet_icon_icon")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[
                    PropertyNames.DISPLAY_NEW_DOCUMENT_PORTLET_ICON
                  ] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.DISPLAY_NEW_DOCUMENT_PORTLET_ICON
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("display_new_document_portlet_icon_helper_text")}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={"Display topics in the document taxonomy portlet?"}
            control={
              <Checkbox
                value=""
                checked={
                  properties[
                    PropertyNames.DISPLAY_TOPICS_IN_DOC_TAX_PORTLET
                  ] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.DISPLAY_TOPICS_IN_DOC_TAX_PORTLET
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {
              "If topics are displayed in the taxonomy selector in the document taxonomy portlet"
            }
          </FormHelperText>
        </FormControl>

        {/* <FormControl>
          <FormControlLabel
            label={t("discussions_enabled")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.DISCUSSIONS_ENABLED] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.DISCUSSIONS_ENABLED
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("discussions_enabled_helper_text")}
          </FormHelperText>
        </FormControl> */}
      </div>
    </div>
  );
}

export default GeneralSettings;
